import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';
import Avatar from '../components/Avatar'; 
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { fetchSubscriptions, cancelSubscription, resumeSubscription, openBillingPortal } from '../services/subscriptionService';
import CloudSettingsManager from '../components/CloudSettingsManager';

import styles from '../style/ProfilePage.module.css';

function ProfilePage() {
  const { user, session } = useAuth(); 
  const navigate = useNavigate();

  // Profil-Daten States
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Passwort-States
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profil-Update-Status
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [updateError, setUpdateError] = useState("");

  // Abonnement-States
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsError, setSubsError] = useState("");
  const [subsData, setSubsData] = useState(null);
  const [subsNotice, setSubsNotice] = useState("");

  // --- Custom Delete Modal States ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  const iconForProduct = (name) => {
    if (!name) return '📦';
    const key = String(name).toLowerCase();
    if (key.includes('chat')) return '🤖';
    if (key.includes('anom')) return '📈';
    return '📦';
  };
  
  const labelForProduct = (name) => {
    if (!name) return 'Abo';
    const key = String(name).toLowerCase();
    if (key === 'chatbot' || key.includes('chat')) return 'Chatbot';
    if (key === 'anomaly' || key.includes('anom')) return 'Anomalieerkennung';
    return name;
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    try {
      const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
      return d.toLocaleDateString('de-DE');
    } catch (_) {
      return '-';
    }
  };

  const nextRenewal = (sub) => {
    if (!sub) return null;
    if (sub.status === 'trialing' && sub.trial_end) return sub.trial_end;
    return sub.current_period_end || null;
  };

  const handleCancel = async (subscriptionId) => {
    try {
      setSubsNotice("");
      setSubsError("");
      await cancelSubscription(subscriptionId, session?.access_token);
      setSubsNotice('Abo wird am Ende der aktuellen Periode gekündigt.');
      await loadSubscriptions();
    } catch (e) {
      setSubsError('Kündigung fehlgeschlagen.');
    }
  };

  const handleResume = async (subscriptionId) => {
    try {
      setSubsNotice("");
      setSubsError("");
      await resumeSubscription(subscriptionId, session?.access_token);
      setSubsNotice('Kündigung wurde zurückgenommen.');
      await loadSubscriptions();
    } catch (e) {
      setSubsError('Aktion fehlgeschlagen.');
    }
  };

  const handleOpenPortal = async () => {
    try {
      await openBillingPortal(session?.access_token);
    } catch (e) {
      setSubsError('Das Abrechnungsportal konnte nicht geöffnet werden.');
    }
  };

  useEffect(() => {
    if (user) {
      getProfile();
      loadSubscriptions();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const loadSubscriptions = async () => {
    try {
      setSubsLoading(true);
      setSubsError("");
      const data = await fetchSubscriptions(session?.access_token);
      setSubsData(data);
    } catch (e) {
      console.error('Fehler beim Laden der Abos:', e?.message || e);
      setSubsError('Abonnements konnten nicht geladen werden.');
    } finally {
      setSubsLoading(false);
    }
  };

  const getProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select(`full_name, avatar_url`)
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setFullName(data.full_name || '');
        setAvatarUrl(data.avatar_url || '');
      }
    } catch (error) {
      console.error('Fehler beim Laden des Profils:', error.message);
      setUpdateError("Profil konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (newAvatarUrl = avatarUrl) => {
    setUpdateSuccess("");
    setUpdateError("");
    try {
      const updates = {
        id: user.id,
        full_name: fullName,
        avatar_url: newAvatarUrl,
        email: user.email,
      };

      const { error } = await supabase.from('profiles').upsert(updates);
      if (error) throw error;

      const { error: userUpdateError } = await supabase.auth.updateUser({
        data: { full_name: fullName }
      });
      if (userUpdateError) throw userUpdateError;

      setAvatarUrl(newAvatarUrl);
      setUpdateSuccess("Profil erfolgreich aktualisiert!");
      setTimeout(() => setUpdateSuccess(""), 3000);

    } catch (error) {
      console.error('Fehler beim Aktualisieren des Profils:', error.message);
      setUpdateError("Profil konnte nicht aktualisiert werden.");
    }
  };

  const handleRemoveAvatar = async () => {
    setUpdateSuccess("");
    setUpdateError("");

    if (!avatarUrl) return;

    try {
      const { error: removeError } = await supabase.storage
        .from('avatars')
        .remove([avatarUrl]);
      
      if (removeError) throw removeError;

      const updates = {
        id: user.id,
        avatar_url: null,
        email: user.email,
        full_name: fullName,
      };

      const { error: updateError } = await supabase.from('profiles').upsert(updates);
      if (updateError) throw updateError;

      setAvatarUrl(null); 
      setUpdateSuccess("Profilbild erfolgreich entfernt.");
      setTimeout(() => setUpdateSuccess(""), 3000);

    } catch (error) {
      console.error('Fehler beim Entfernen des Avatars:', error.message);
      setUpdateError("Profilbild konnte nicht entfernt werden.");
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    const { newPassword, confirmPassword } = e.target.elements;

    if (newPassword.value !== confirmPassword.value) {
      setPasswordError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword.value,
    });

    if (updateError) {
      setPasswordError("Fehler beim Aktualisieren des Passworts: " + (updateError.message || ''));
    } else {
      setPasswordSuccess("Passwort erfolgreich geändert.");
      e.target.reset();
      setTimeout(() => setPasswordSuccess(""), 3000);
    }
  };

  // --- Custom Modal Logic ---
  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmationText("");
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmationText("");
    setIsDeleting(false);
  };

  const confirmDeleteAccount = async () => {
    if (deleteConfirmationText !== "LÖSCHEN") return;

    try {
      setIsDeleting(true);

      const { error } = await supabase.rpc('delete_user_account');

      if (error) {
        throw error;
      }

      await supabase.auth.signOut();
      
      navigate("/");
    } catch (error) {
      console.error("Delete Account Error:", error);
      alert("Fehler beim Löschen: " + (error.message || "Unbekannter Fehler"));
      setIsDeleting(false);
    }
  };

  const subscriptions = subsData?.subscriptions || [];
  const uniqueProducts = Array.from(
    new Set(
      subscriptions.flatMap((sub) =>
        sub?.product_names && sub.product_names.length > 0 ? sub.product_names : []
      )
    )
  );
  const displayName = fullName?.trim() || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Nutzer';

  if (loading) {
    return <div>Profil wird geladen...</div>;
  }

  return (
    <div className={styles.profileContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTextBlock}>
            <p className={styles.eyebrow}>Kontoübersicht</p>
            <h1 className={styles.title}>Profil & Sicherheit</h1>
            <p className={styles.subtitle}>
              Verwalten Sie Ihre Kontodaten, Passwörter, Abonnements und Cloud-Einstellungen an einem Ort.
            </p>
          </div>

          <div className={styles.heroStats}>
            <div className={styles.heroStatCard}>
              <span className={styles.heroStatLabel}>Aktive Abos</span>
              <strong className={styles.heroStatValue}>{subsLoading ? '...' : subscriptions.length}</strong>
            </div>
            <div className={styles.heroStatCard}>
              <span className={styles.heroStatLabel}>Produkte</span>
              <strong className={styles.heroStatValue}>{subsLoading ? '...' : uniqueProducts.length}</strong>
            </div>
            <div className={styles.heroStatCard}>
              <span className={styles.heroStatLabel}>Konto</span>
              <strong className={styles.heroStatValue}>Aktiv</strong>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.sectionsGrid}>
        <section className={`${styles.sectionCard} ${styles.sectionSpanHalf}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Profil</h3>
              <p className={styles.sectionDescription}>Aktualisieren Sie Name, E-Mail-Ansicht und Ihr Profilbild.</p>
            </div>
          </div>

          <div className={styles.profileHero}>
            <div className={styles.avatarBlock}>
              <Avatar
                url={avatarUrl}
                onUpload={(newPath) => {
                  handleUpdateProfile(newPath);
                }}
                onRemove={handleRemoveAvatar}
              />
            </div>

            <div className={styles.profileIdentity}>
              <p className={styles.eyebrow}>Aktiver Nutzer</p>
              <h3 className={styles.profileName}>{displayName}</h3>
              <p className={styles.profileMeta}>{user.email}</p>
              <p className={styles.profileHint}>Passen Sie hier Ihre persönlichen Kontodaten an.</p>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }} className={styles.profileForm}>
            {updateSuccess && <p className={styles.successText}>{updateSuccess}</p>}
            {updateError && <p className={styles.errorText}>{updateError}</p>}

            <div className={styles.formGroup}>
              <label htmlFor="email" className={styles.formLabel}>E-Mail</label>
              <input
                id="email"
                type="text"
                value={user.email}
                disabled
                className={styles.profileInput}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="fullName" className={styles.formLabel}>Vollständiger Name</label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ihr Name"
                className={styles.profileInput}
              />
            </div>

            <button type="submit" className={styles.saveBtn}>Profil speichern</button>
          </form>
        </section>

        <section className={`${styles.sectionCard} ${styles.sectionSpanHalf}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Passwort ändern</h3>
              <p className={styles.sectionDescription}>Sichern Sie Ihr Konto mit einem neuen Passwort.</p>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className={styles.profileForm}>
            {passwordError && <p className={styles.errorText}>{passwordError}</p>}
            {passwordSuccess && <p className={styles.successText}>{passwordSuccess}</p>}

            <div className={styles.formGroup}>
              <label htmlFor="newPassword" className={styles.formLabel}>Neues Passwort</label>
              <div className={styles.inputWrapper}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  placeholder="Neues Passwort"
                  required
                  className={`${styles.profileInput} ${styles.inputWithToggle}`}
                />
                <button
                  type="button"
                  className={styles.passwordToggleIcon}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}
                >
                  {showNewPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                </button>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword" className={styles.formLabel}>Neues Passwort bestätigen</label>
              <div className={styles.inputWrapper}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Neues Passwort bestätigen"
                  required
                  className={`${styles.profileInput} ${styles.inputWithToggle}`}
                />
                <button
                  type="button"
                  className={styles.passwordToggleIcon}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Passwort ausblenden' : 'Passwort anzeigen'}
                >
                  {showConfirmPassword ? <VisibilityOff fontSize="inherit" /> : <Visibility fontSize="inherit" />}
                </button>
              </div>
            </div>

            <button type="submit" className={styles.saveBtn}>Passwort speichern</button>
          </form>
        </section>

        <section className={`${styles.sectionCard} ${styles.sectionSpanFull}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Abonnements</h3>
              <p className={styles.sectionDescription}>Behalten Sie Laufzeiten, Produkte und Abrechnungsoptionen im Blick.</p>
            </div>
          </div>
          <div className={styles.subscriptionSection}>
            {subsError && <p className={styles.errorText}>{subsError}</p>}
            {subsNotice && <p className={styles.successText}>{subsNotice}</p>}
            {subsLoading ? (
              <div className={styles.hintText}>Abonnements werden geladen...</div>
            ) : (
              <>
                {!subsError && subscriptions.length === 0 && (
                  <div className={styles.hintText}>Kein aktives Abonnement gefunden.</div>
                )}
                {subscriptions.map((sub) => (
                  <div key={sub.id || Math.random()} className={styles.subscriptionCard}>
                    <div className={styles.subscriptionHeader}>
                      <div className={styles.pillContainer}>
                        {(sub.product_names && sub.product_names.length > 0 ? sub.product_names : ['Abo']).map((p) => (
                          <span key={p} className={styles.pill}>
                            <span className={styles.pillIcon}>{iconForProduct(p)}</span>
                            {labelForProduct(p)}
                          </span>
                        ))}
                      </div>
                      <span className={styles.statusBadge}>
                        {sub.cancel_at_period_end
                          ? 'Gekündigt (läuft bis Ende der Periode)'
                          : sub.status === 'trialing'
                            ? 'Testphase'
                            : (sub.status || 'Unbekannt')}
                      </span>
                    </div>

                    <div className={styles.dateRow}>
                      <div className={styles.dateBox}>
                        <div className={styles.dateLabel}>Begonnen am</div>
                        <div className={styles.dateValue}>{formatDate(sub.start_date)}</div>
                      </div>
                      <div className={styles.dateBox}>
                        <div className={styles.dateLabel}>Nächste Verlängerung</div>
                        <div className={styles.dateValue}>{formatDate(nextRenewal(sub))}</div>
                      </div>
                    </div>

                    <div className={styles.actionsRow}>
                      {sub.id && (
                        sub.cancel_at_period_end ? (
                          <button type="button" className={styles.btnSecondary} onClick={() => handleResume(sub.id)}>
                            Kündigung zurücknehmen
                          </button>
                        ) : (
                          <button type="button" className={styles.btnDanger} onClick={() => handleCancel(sub.id)}>
                            Kündigen zum Periodenende
                          </button>
                        )
                      )}
                      <button type="button" className={styles.btnSecondary} onClick={handleOpenPortal}>
                        Rechnungen & Zahlungsmittel verwalten
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>

        <section className={`${styles.sectionCard} ${styles.sectionSpanWide}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Cloud-Einstellungen</h3>
              <p className={styles.sectionDescription}>Verwalten Sie Verbindungen und Konfigurationen für Ihre Berichte.</p>
            </div>
          </div>
          <CloudSettingsManager user={user} />
        </section>

        <section className={`${styles.sectionCard} ${styles.sectionSpanNarrow} ${styles.dangerZoneCard}`}>
          <div className={styles.sectionHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Gefahrenzone</h3>
              <p className={styles.sectionDescription}>Löschen Sie Ihr Konto und alle zugehörigen Daten unwiderruflich.</p>
            </div>
          </div>
          <div className={styles.dangerZoneContent}>
            <p className={styles.dangerZoneText}>
              Diese Aktion entspricht Ihrem Recht auf Vergessenwerden gemäß DSGVO und kann nicht rückgängig gemacht werden.
            </p>
            <button
              onClick={openDeleteModal}
              type="button"
              className={`${styles.saveBtn} ${styles.dangerCta}`}
            >
              Konto unwiderruflich löschen
            </button>
          </div>
        </section>
      </div>

      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <h2 className={styles.modalTitle}>Konto wirklich löschen?</h2>
            <p className={styles.modalText}>
              Diese Aktion löscht Ihr Konto, alle gespeicherten Daten und aktiven Abonnements <strong>unwiderruflich</strong>.
              Dies kann nicht rückgängig gemacht werden.
            </p>
            <p className={styles.modalPrompt}>Bitte geben Sie "LÖSCHEN" ein, um zu bestätigen:</p>

            <input
              type="text"
              value={deleteConfirmationText}
              onChange={(e) => setDeleteConfirmationText(e.target.value)}
              placeholder="LÖSCHEN"
              className={styles.modalInput}
            />

            <div className={styles.modalActions}>
              <button onClick={closeDeleteModal} type="button" className={styles.modalSecondaryButton}>
                Abbrechen
              </button>

              <button
                onClick={confirmDeleteAccount}
                type="button"
                disabled={deleteConfirmationText !== "LÖSCHEN" || isDeleting}
                className={`${styles.saveBtn} ${styles.modalDangerButton}`}
              >
                {isDeleting ? "Wird gelöscht..." : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
